import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldPlus, ShieldCheck, Hand, ClipboardCheck, TriangleAlert, Lock } from "lucide-react";
import { api, errMessage } from "@/lib/api";
import { PageTitle, Loading, ErrorBox, Empty, Section } from "@/components/common";
import { Take5Card, InspectionCard } from "@/components/Cards";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtDateTime } from "@/lib/time";
import { selectCls } from "@/pages/Take5New";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const GROUPS = [
  { key: "take5", title: "Take 5", icon: ShieldPlus, items: [
    ["take5_today", "Take 5 du jour", ShieldPlus],
    ["authorized_today", "Autorisés aujourd'hui", ShieldCheck],
    ["stopped_today", "Arrêts aujourd'hui", Hand],
  ] },
  { key: "equipment", title: "Contrôle équipement", icon: ClipboardCheck, items: [
    ["inspections_today", "Inspections du jour", ClipboardCheck],
    ["anomalies_today", "Anomalies du jour", TriangleAlert],
    ["blocked_equipment", "Équipements bloqués", Lock],
  ] },
];

const BlockedRow = ({ e }) => (
  <Link to={`/equipements/${e.id}`} data-testid={`blocked-${e.code}`} className="flex items-center justify-between gap-2 rounded-lg border border-red-500/50 p-3 hover:bg-red-950/30">
    <span><span className="font-data text-amber-300">{e.code}</span> · {e.name}<br /><span className="text-xs text-muted-foreground">{e.site?.name}</span></span>
    <StatusBadge status="bloque" />
  </Link>
);

const kpiRows = (k, data) => {
  const t5 = data.today.take5s;
  const insp = data.today.inspections;
  return {
    take5_today: t5.map((t) => <Take5Card key={t.id} t={t} showUser />),
    authorized_today: t5.filter((t) => t.decision === "autorisee").map((t) => <Take5Card key={t.id} t={t} showUser />),
    stopped_today: t5.filter((t) => t.decision === "arretee").map((t) => <Take5Card key={t.id} t={t} showUser />),
    inspections_today: insp.map((i) => <InspectionCard key={i.id} i={i} showUser />),
    anomalies_today: insp.filter((i) => (i.anomaly_count || 0) > 0).map((i) => <InspectionCard key={i.id} i={i} showUser />),
    blocked_equipment: data.blocked_equipment.map((e) => <BlockedRow key={e.id} e={e} />),
  }[k];
};

const KpiDialog = ({ kpi, data, onClose }) => {
  const rows = kpi ? kpiRows(kpi[0], data) : [];
  return (
    <Dialog open={!!kpi} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="kpi-dialog">
        <DialogHeader><DialogTitle>{kpi?.[1]} ({rows.length})</DialogTitle></DialogHeader>
        {!rows.length && <Empty>Aucun élément.</Empty>}
        <div className="space-y-2" onClick={(e) => { if (e.target.closest("a")) onClose(); }}>{rows}</div>
      </DialogContent>
    </Dialog>
  );
};

const KpiGroup = ({ g, counts, onOpen }) => (
  <section data-testid={`kpi-group-${g.key}`} className="rounded-2xl border border-border bg-muted/30 p-3 sm:p-4">
    <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
      <g.icon className="h-4 w-4 text-amber-500" /> {g.title}
    </h2>
    <div className="grid grid-cols-3 gap-2 sm:gap-3">
      {g.items.map(([k, label, Icon]) => (
        <button type="button" key={k} data-testid={`count-${k}`} onClick={() => onOpen([k, label])}
          className={`rounded-xl border bg-card p-3 text-left transition-colors hover:border-amber-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-500 sm:p-4 ${k === "blocked_equipment" && counts[k] > 0 ? "border-red-500" : "border-border"}`}>
          <Icon className="h-5 w-5 text-amber-400" />
          <p className="mt-2 font-data text-2xl font-bold sm:text-3xl">{counts[k]}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </button>
      ))}
    </div>
  </section>
);

export default function Supervision() {
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [kpi, setKpi] = useState(null);
  useEffect(() => { api.get("/referentials").then((r) => setSites(r.data.sites)).catch(() => {}); }, []);
  const load = () => {
    setError("");
    setData(null);
    api.get("/supervision", { params: { site_id: siteId } }).then((r) => setData(r.data)).catch((e) => setError(errMessage(e)));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [siteId]);

  return (
    <div>
      <PageTitle kicker="Pilotage" title="Supervision" testId="supervision-title">
        <select data-testid="supervision-site-filter" className={`${selectCls} w-64`} value={siteId} onChange={(e) => setSiteId(e.target.value)}>
          <option value="">Tous les sites</option>
          {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </PageTitle>
      {error && <ErrorBox message={error} onRetry={load} />}
      {!data && !error && <Loading />}
      {data && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {GROUPS.map((g) => <KpiGroup key={g.key} g={g} counts={data.counts} onOpen={setKpi} />)}
          </div>
          <KpiDialog kpi={kpi} data={data} onClose={() => setKpi(null)} />
          <p className="text-xs text-muted-foreground">« Du jour » : date locale de chaque site. Valeurs calculées à partir des enregistrements réels.</p>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 [&>*]:min-w-0">
            <Section title="Équipements bloqués" icon={Lock} testId="supervision-blocked">
              {!data.blocked_equipment.length && <Empty>Aucun équipement bloqué.</Empty>}
              <div className="space-y-2">{data.blocked_equipment.map((e) => <BlockedRow key={e.id} e={e} />)}</div>
            </Section>
            <Section title="Anomalies récentes" icon={TriangleAlert} testId="supervision-anomalies">
              {!data.anomalies.length && <Empty>Aucune anomalie.</Empty>}
              <div className="space-y-2">{data.anomalies.map((a, i) => (
                <Link key={i} to={`/inspections/${a.inspection_id}`} className="block rounded-lg border border-border p-3 hover:border-amber-500/50">
                  <div className="flex flex-wrap items-center gap-2"><StatusBadge status={a.criticality} /><span className="font-data text-sm text-amber-300">{a.equipment.code}</span><span className="text-sm font-semibold">{a.label}</span></div>
                  <p className="mt-1 line-clamp-2 text-sm text-slate-300">{a.comment}</p>
                  <p className="mt-1 font-data text-xs text-muted-foreground">{fmtDateTime(a.at, a.site)} · {a.user_name}</p>
                </Link>))}</div>
            </Section>
            <Section title="Tâches arrêtées" icon={Hand} testId="supervision-stopped">
              {!data.stopped_take5s.length && <Empty>Aucune tâche arrêtée.</Empty>}
              <div className="space-y-2">{data.stopped_take5s.map((t) => <Take5Card key={t.id} t={t} showUser />)}</div>
            </Section>
            <Section title="Take 5 récents" icon={ShieldPlus} testId="supervision-recent-take5">
              {!data.recent_take5s.length && <Empty>Aucun Take 5.</Empty>}
              <div className="space-y-2">{data.recent_take5s.map((t) => <Take5Card key={t.id} t={t} showUser />)}</div>
            </Section>
            <Section title="Inspections récentes" icon={ClipboardCheck} testId="supervision-recent-inspections">
              {!data.recent_inspections.length && <Empty>Aucune inspection.</Empty>}
              <div className="space-y-2">{data.recent_inspections.map((i) => <InspectionCard key={i.id} i={i} showUser />)}</div>
            </Section>
          </div>
        </div>
      )}
    </div>
  );
}
